import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { toast } from "sonner";
import { formatUSD, monthName } from "@/lib/format";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

export default function YoyComparison() {
  const cy = new Date().getFullYear();
  const [yearA, setYearA] = useState(cy - 1);
  const [yearB, setYearB] = useState(cy);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (a, b) => {
    setLoading(true);
    try {
      const res = await api.get("/report/compare", { params: { year_a: a, year_b: b } });
      setData(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Initial fetch only — subsequent fetches happen via the Compare button.
    load(parseInt(yearA, 10), parseInt(yearB, 10));
    // yearA/yearB intentionally excluded; user clicks Compare to refetch
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = () => load(parseInt(yearA, 10), parseInt(yearB, 10));

  if (!data) return <div className="p-6 text-sm text-neutral-500">Loading…</div>;

  const merged = [];
  const monthsA = Object.fromEntries((data.monthly_a || []).map((m) => [m.month.slice(5), m]));
  const monthsB = Object.fromEntries((data.monthly_b || []).map((m) => [m.month.slice(5), m]));
  for (let m = 1; m <= 12; m++) {
    const key = String(m).padStart(2, "0");
    merged.push({
      label: monthName(`${data.year_b}-${key}`),
      net_a: monthsA[key]?.net || 0,
      net_b: monthsB[key]?.net || 0,
    });
  }

  const ROWS = [
    ["Closed Transactions", "transaction_count", false],
    ["Total Sales Volume", "total_sales", true],
    ["Gross Commission", "gross_commission", true],
    ["Broker Fees / Splits", "broker_fees", true],
    ["Net Commission", "net_commission", true],
    ["Total Expenses", "total_expenses", true],
    ["Net Profit", "net_profit", true],
  ];

  return (
    <div className="border border-neutral-200 p-6" data-testid="yoy-comparison">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">Year-over-Year</div>
          <div className="font-serif text-2xl">Performance Comparison</div>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Year A</Label>
            <Input type="number" value={yearA} onChange={(e) => setYearA(e.target.value)}
              className="mt-1 w-24 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900 font-mono" data-testid="yoy-year-a" />
          </div>
          <span className="font-mono text-neutral-400 mb-2">vs</span>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Year B</Label>
            <Input type="number" value={yearB} onChange={(e) => setYearB(e.target.value)}
              className="mt-1 w-24 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900 font-mono" data-testid="yoy-year-b" />
          </div>
          <Button onClick={apply} disabled={loading} className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white h-10" data-testid="yoy-apply-button">
            {loading ? "Loading…" : "Compare"}
          </Button>
        </div>
      </div>

      {/* Side-by-side table */}
      <div className="border border-neutral-300 mb-6">
        <table className="w-full text-sm">
          <thead className="bg-neutral-900 text-white">
            <tr>
              <th className="text-left font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">Metric</th>
              <th className="text-right font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">{data.year_a}</th>
              <th className="text-right font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">{data.year_b}</th>
              <th className="text-right font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">Δ</th>
              <th className="text-right font-sans font-medium uppercase tracking-[0.12em] text-[10px] px-3 py-2">%</th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map(([label, key, money]) => {
              const d = data.deltas[key];
              const delta = d?.delta ?? 0;
              const pct = d?.pct;
              const positiveIsGood = !["broker_fees", "total_expenses"].includes(key);
              const better = positiveIsGood ? delta >= 0 : delta <= 0;
              const Icon = delta === 0 ? Minus : (better ? ArrowUpRight : ArrowDownRight);
              const color = delta === 0 ? "text-neutral-400" : (better ? "text-emerald-700" : "text-red-700");
              return (
                <tr key={key} className="border-t border-neutral-200" data-testid={`yoy-row-${key}`}>
                  <td className="px-3 py-2">{label}</td>
                  <td className="px-3 py-2 font-mono text-right">{money ? formatUSD(d?.a) : d?.a}</td>
                  <td className="px-3 py-2 font-mono text-right font-semibold">{money ? formatUSD(d?.b) : d?.b}</td>
                  <td className={`px-3 py-2 font-mono text-right ${color}`}>
                    <span className="inline-flex items-center gap-1 justify-end">
                      <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                      {money ? formatUSD(Math.abs(delta)) : Math.abs(delta)}
                    </span>
                  </td>
                  <td className={`px-3 py-2 font-mono text-right ${color}`}>
                    {pct === null || pct === undefined ? "—" : `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Monthly net commission chart */}
      <div className="border border-neutral-300 p-4 h-[320px]">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">Monthly Net Commission</div>
        <ResponsiveContainer width="100%" height="90%">
          <BarChart data={merged}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#525252", fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#E5E5E5" }} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: "#525252", fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
            <Tooltip
              contentStyle={{ background: "#0A0A0A", color: "#FFF", border: "none", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 11 }}
              formatter={(v) => formatUSD(v)}
              cursor={{ fill: "#FAFAFA" }}
            />
            <Legend wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 11, color: "#525252" }} />
            <Bar dataKey="net_a" fill="#A3A3A3" name={`${data.year_a}`} />
            <Bar dataKey="net_b" fill="#0A0A0A" name={`${data.year_b}`} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
